import React from 'react';
import {StyleSheet, View, useWindowDimensions} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import CCText from '@/Atoms/CCText';
import {WIDE_BREAKPOINT, lbColors, lbRadius} from '../leaderboardTheme';
import {LeaderboardPrizeVM, MilestoneVM} from '../types';

interface Props {
  milestones: MilestoneVM[];
  // Weekly Top-3 leaderboard prize (Prizes v2) — a separate, optional field
  // from milestones[] (see types.ts): absent hides the leaderboard card
  // rather than guessing at copy ops hasn't configured yet.
  leaderboardPrize?: LeaderboardPrizeVM;
}

/**
 * Rewards section — one card per prize *kind*: the deterministic weekly
 * leaderboard Top-3 award, the weekly 7-day-streak lucky draw, and the
 * monthly 30-day-streak grand lucky draw (dark-gold hero treatment).
 * Design source: scratchpad/engagement_canvas.dc.html (#cc-prizes,
 * `rewardCards`) — Prizes v2 revamp of the earlier per-milestone cards.
 *
 * Data-driven: the leaderboard card comes from `leaderboardPrize`, the two
 * draw cards from the weekly/monthly entries in `milestones[]` — no
 * hardcoded prize names, day thresholds or eligibility copy.
 */
export default function PrizeCards({milestones, leaderboardPrize}: Props) {
  const {width} = useWindowDimensions();
  const wide = width >= WIDE_BREAKPOINT;
  // Exclude the week_2/week_3 checkpoints — they carry period 'weekly' too,
  // and config order isn't guaranteed to put weekly_draw first (ops-edited).
  const weeklyMilestone = milestones.find(
    m => m.period === 'weekly' && m.kind !== 'week_checkpoint',
  );
  const monthlyMilestone = milestones.find(
    m => m.period === 'monthly' && m.kind !== 'week_checkpoint',
  );

  return (
    <View style={styles.section}>
      <View style={styles.header}>
        <CCText fontSize={17} fontWeight="bold" color={lbColors.ink}>
          Rewards
        </CCText>
        <CCText fontSize={10.5} fontWeight="semibold" color={lbColors.faint}>
          draws give a chance to win · winners announced in-app every Monday
        </CCText>
      </View>
      <View style={[styles.grid, wide && styles.gridWide]}>
        {leaderboardPrize ? (
          <LeaderboardCard prize={leaderboardPrize} wide={wide} />
        ) : null}
        {weeklyMilestone ? (
          <DrawCard milestone={weeklyMilestone} wide={wide} hero={false} />
        ) : null}
        {monthlyMilestone ? (
          <DrawCard milestone={monthlyMilestone} wide={wide} hero />
        ) : null}
      </View>
    </View>
  );
}

function LeaderboardCard({
  prize,
  wide,
}: {
  prize: LeaderboardPrizeVM;
  wide: boolean;
}) {
  return (
    <View
      style={[styles.card, wide && styles.cardWide, styles.cardWhiteBorder]}>
      <View style={styles.cardTop}>
        <View style={[styles.tag, {backgroundColor: lbColors.goldSoft}]}>
          <CCText fontSize={9} fontWeight="bold" color={lbColors.youChipSub}>
            LEADERBOARD · WEEKLY
          </CCText>
        </View>
        <CCText fontSize={22}>🥇</CCText>
      </View>
      <View style={styles.cardMiddle}>
        <CCText fontSize={16} fontWeight="bold" color={lbColors.ink}>
          {prize.title} · Top {prize.rankTo}
        </CCText>
        <CCText
          fontSize={11}
          fontWeight="semibold"
          color={lbColors.muted}
          style={styles.rule}>
          Finish Top {prize.rankTo} by wins this week. Minimum{' '}
          {prize.minMetricValue} wins to qualify.
        </CCText>
      </View>
      <View style={[styles.chip, {backgroundColor: lbColors.bgAlt}]}>
        <CCText fontSize={10} fontWeight="bold" color={lbColors.inkSoft}>
          Decided by rank — no luck needed
        </CCText>
      </View>
    </View>
  );
}

function DrawCard({
  milestone,
  wide,
  hero,
}: {
  milestone: MilestoneVM;
  wide: boolean;
  hero: boolean;
}) {
  const eligible = milestone.achievedThisPeriod;
  const tag = hero ? 'LUCKY DRAW · MONTHLY' : 'LUCKY DRAW · WEEKLY';
  const icon = hero ? '🏆' : '🎟️';
  const rule = hero
    ? `The grand draw — every ${milestone.days}-day streak holder enters.`
    : `Every ${milestone.days}-day streak holder gets a ticket in Sunday's draw.`;
  const chipText = eligible
    ? hero
      ? "You're in the grand draw 🎉"
      : "You're in this week's draw 🎉"
    : hero
    ? `${milestone.daysLeft} days to entry`
    : `${milestone.daysLeft} ${
        milestone.daysLeft === 1 ? 'day' : 'days'
      } to your ticket`;

  const tagBg = hero ? 'rgba(245,195,60,0.16)' : lbColors.tealSoft;
  const tagColor = hero ? lbColors.gold : lbColors.tealDeep;
  const chipBg = hero
    ? 'rgba(245,195,60,0.16)'
    : eligible
    ? lbColors.greenBg
    : lbColors.riskBg;
  const chipColor = hero
    ? lbColors.gold
    : eligible
    ? lbColors.greenInk
    : lbColors.drawPendingText;

  const content = (
    <>
      <View style={styles.cardTop}>
        <View style={[styles.tag, {backgroundColor: tagBg}]}>
          <CCText fontSize={9} fontWeight="bold" color={tagColor}>
            {tag}
          </CCText>
        </View>
        <CCText fontSize={22}>{icon}</CCText>
      </View>
      <View style={styles.cardMiddle}>
        <CCText
          fontSize={16}
          fontWeight="bold"
          color={hero ? lbColors.gold : lbColors.ink}>
          {milestone.prize.title}
        </CCText>
        <CCText
          fontSize={11}
          fontWeight="semibold"
          color={hero ? lbColors.darkText : lbColors.muted}
          style={styles.rule}>
          {rule}
        </CCText>
      </View>
      <View style={[styles.chip, {backgroundColor: chipBg}]}>
        <CCText fontSize={10} fontWeight="bold" color={chipColor}>
          {chipText}
        </CCText>
      </View>
    </>
  );

  if (hero) {
    return (
      <LinearGradient
        colors={[lbColors.darkPanel, lbColors.darkPanelDeep]}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 1}}
        style={[
          styles.card,
          wide && styles.cardWide,
          {borderColor: lbColors.goldDeep, borderWidth: 2},
        ]}>
        {content}
      </LinearGradient>
    );
  }

  return (
    <View
      style={[
        styles.card,
        wide && styles.cardWide,
        {borderColor: eligible ? lbColors.green : lbColors.line},
      ]}>
      {content}
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    backgroundColor: lbColors.card,
    borderRadius: lbRadius.xl,
    padding: 20,
    marginTop: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 9,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  grid: {gap: 12},
  gridWide: {flexDirection: 'row'},
  card: {
    borderRadius: lbRadius.lg,
    borderWidth: 2,
    padding: 16,
    gap: 10,
    minHeight: 150,
  },
  cardWide: {flex: 1},
  cardWhiteBorder: {borderColor: lbColors.line, backgroundColor: lbColors.card},
  cardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  tag: {paddingHorizontal: 10, paddingVertical: 4, borderRadius: 10},
  // Grows to fill the card's remaining height so the chip below always sits
  // at the bottom, regardless of how many lines the rule text wraps to.
  cardMiddle: {flex: 1},
  rule: {marginTop: 4, lineHeight: 15},
  chip: {
    alignSelf: 'flex-start',
    paddingHorizontal: 11,
    paddingVertical: 4,
    borderRadius: 10,
  },
});
